import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import i18n from 'utils/i18n';
import { SubmitHandler, useForm } from 'react-hook-form';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import React, { useState } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Typography,
} from '@mui/material';
import * as yup from 'yup';
import TextFieldStyled from '../../shared/TextFieldStyled';
import SelectStyled from '../../shared/SelectStyled';
import { DocumentUploadBody } from 'models/DocumentLibrary';
import { useAdminDocumentLibrary } from 'hooks/dokumentLibrary/useAdminDocumentLibrary';
import { formatBytes } from 'utils/numberFormatter';
import { AdminDocumentUploadFormProps } from 'models/Admin/AdminDocumentLibrary';

const schema = yup.object().shape({
    description: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
});

export const AdminDocumentUploadForm: React.FC<AdminDocumentUploadFormProps> = (
    props
) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<DocumentUploadBody>({
        resolver: yupResolver(schema),
    });

    const { t } = useTranslation();
    const { t: tCustom } = useTranslation('documentLibrary', {
        keyPrefix: 'UPLOAD_DIALOG',
    });

    const [submitIsLoading, setSubmitIsLoading] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File>(null);
    const [isActive, setIsActive] = useState<string>('0');

    const { uploadDocument } = useAdminDocumentLibrary();

    const onSubmit: SubmitHandler<DocumentUploadBody> = (data) => {
        setSubmitIsLoading(true);

        uploadDocument({
            document: selectedFile,
            description: data.description,
            is_active: isActive,
        })
            .then(() => {
                SnackbarUtils.success(
                    t('NOTIFICATION.DOCUMENT_UPLOADED').toString()
                );
                props.submitSuccess();
                setSubmitIsLoading(false);
            })
            .catch((err) => {
                SnackbarUtils.error(
                    t('NOTIFICATION.DOCUMENT_UPLOAD_UNSUCCESSFUL').toString()
                );
                setSubmitIsLoading(false);
            });
    };

    const handleSetActive = (value: string) => {
        setIsActive(value);
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    {/* File */}
                    <FormControl fullWidth>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: 'bold',
                            }}
                        >
                            <PrimaryBtn variant="contained" component="label">
                                {tCustom('CHOOSE').toString()}
                                <input
                                    {...register('document')}
                                    hidden
                                    type="file"
                                    accept=".pdf"
                                    onChange={(event) => {
                                        setSelectedFile(event.target.files[0]);
                                    }}
                                />
                            </PrimaryBtn>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Typography>
                                    {selectedFile?.name
                                        ? selectedFile?.name
                                        : tCustom('NONE_SELECTED').toString()}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{ color: '#6f6f6f', fontSize: '12px' }}
                                >
                                    {selectedFile?.size
                                        ? formatBytes(selectedFile?.size)
                                        : ''}
                                </Typography>
                            </Box>
                        </Box>
                    </FormControl>

                    {/* Description */}
                    <FormControl fullWidth>
                        <Typography>
                            {tCustom('DESCRIPTION').toString()}
                        </Typography>
                        <TextFieldStyled
                            {...register('description')}
                            sx={{
                                backgroundColor: 'white',
                                '& .MuiFilledInput-input': {
                                    padding: '0px !important',
                                },
                                '& .MuiFilledInput-root': {
                                    minHeight: '50px',

                                    padding: '5px 5px 5px 5px !important',
                                    '@media (max-width: 600px)': {
                                        padding: '2px 0px 2px 5px !important',
                                    },
                                },
                            }}
                            multiline
                            minRows={3}
                            maxRows={3}
                            error={!!errors.description}
                        />
                        <FormHelperText error>
                            {t(errors.description?.message)}
                        </FormHelperText>
                    </FormControl>

                    {/* Visibility */}
                    <FormControl fullWidth variant="filled">
                        <Typography>{tCustom('ACTIVE').toString()}</Typography>
                        <SelectStyled
                            {...register('is_active', {
                                onChange: (e) =>
                                    handleSetActive(e.target.value),
                            })}
                            sx={{
                                '& .MuiFilledInput-input': {
                                    padding: '10px 10px 11px 10px !important',
                                },
                            }}
                            value={isActive}
                        >
                            <MenuItem value={'0'}>
                                {tCustom('VISIBILITY.INVISIBLE').toString()}
                            </MenuItem>
                            <MenuItem value={'1'}>
                                {tCustom('VISIBILITY.VISIBLE').toString()}
                            </MenuItem>
                        </SelectStyled>
                    </FormControl>
                </Box>
                <Box
                    sx={{
                        justifyContent: 'space-between',
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '16px',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <PrimaryBtn
                            type="submit"
                            disabled={
                                submitIsLoading ||
                                !selectedFile ||
                                !!errors.description
                            }
                        >
                            {tCustom('UPLOAD').toString()}
                        </PrimaryBtn>
                    </Box>
                </Box>
            </form>
        </>
    );
};
